import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BoxesWrapper,
  DescriptionRow1,
  DescriptionRow2,
  NotificationsSectionStyled,
  RealTimeOptions,
  SwitchOptionLabel,
  SwitchOptionsWrapper,
} from 'Features/userProfile/components/NotificationsSection/NotificationsSection.styled'
import { ProfileSectionTitle } from 'Features/userProfile/components/ProfileSection/ProfileSection.styled'

import { useGetNotificationsSettings } from 'Features/userProfile/api/getNotificationsSettings'
import { useUpdateNotificationsSettings } from 'Features/userProfile/api/updateNotificationsSettings'
import { useAppStore } from 'Stores/appStoreModuleFed'

import { Toggle, MultiOption } from '@clickcease/cc-client-components-lib'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { userNotificationsFrequenciesSetup } from 'Types/Notifications'

export const NotificationsSection = () => {
  const { t } = useTranslation('userProfile')
  const { data: limits } = useGetPackagingLimits()
  const { data: notifications } = useGetNotificationsSettings()
  const { mutate } = useUpdateNotificationsSettings()
  const { showToaster } = useAppStore()

  const pmAllowed = limits?.paidMarketingAllowed
  const bmAllowed = limits?.botZappingAllowed

  const paidMarketingInit = [
    { id: 0, status: false, title: t('notifications.daily') },
    { id: 1, status: false, title: t('notifications.weekly') },
    { id: 2, status: false, title: t('notifications.monthly') },
  ]

  const botZappingInit = [
    { id: 0, status: false, title: t('notifications.daily') },
    { id: 1, status: false, title: t('notifications.weekly') },
    { id: 2, status: false, title: t('notifications.monthly') },
  ]

  const [paidMarketingFrequencies, setPaidMarketingFrequencies] = useState([...paidMarketingInit])
  const [botZappingFrequencies, setBotZappingFrequencies] = useState([...botZappingInit])

  useEffect(() => {
    setFrequencies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications])

  const setFrequencies = () => {
    const tempPaidMarketingFrequencies = [...paidMarketingFrequencies]
    const tempBotzappingFrequencies = [...botZappingFrequencies]
    if (notifications?.userNotificationsFrequenciesSetup) {
      if (pmAllowed) {
        tempPaidMarketingFrequencies[0].status =
          notifications?.userNotificationsFrequenciesSetup[0].daily
        tempPaidMarketingFrequencies[1].status =
          notifications?.userNotificationsFrequenciesSetup[0].weekly
        tempPaidMarketingFrequencies[2].status =
          notifications?.userNotificationsFrequenciesSetup[0].monthly

        setPaidMarketingFrequencies(tempPaidMarketingFrequencies)
      }

      if (bmAllowed) {
        tempBotzappingFrequencies[0].status =
          notifications?.userNotificationsFrequenciesSetup[1].daily
        tempBotzappingFrequencies[1].status =
          notifications?.userNotificationsFrequenciesSetup[1].weekly
        tempBotzappingFrequencies[2].status =
          notifications?.userNotificationsFrequenciesSetup[1].monthly

        setBotZappingFrequencies(tempBotzappingFrequencies)
      }
    }
  }

  const onSuccess = () => {
    showToaster(t('notifications.updated_successfully'), 'success')
  }

  const handleChange = (value: boolean, type: string) => {
    const payload = {}
    payload[type] = value
    mutate(payload, { onSuccess })
  }

  const handleOptionChange = (type: 'paidMarketing' | 'botZapping', id: number, value: boolean) => {
    if (type === 'paidMarketing') {
      const tempPaidMarketing = [...paidMarketingFrequencies]
      tempPaidMarketing[id].status = value
      setPaidMarketingFrequencies(tempPaidMarketing)
    } else {
      const tempBotzapping = [...botZappingFrequencies]
      tempBotzapping[id].status = value
      setBotZappingFrequencies(tempBotzapping)
    }

    const frequencies: userNotificationsFrequenciesSetup[] = []

    if (pmAllowed) {
      frequencies.push({
        platform: 'PaidMarketing',
        daily: paidMarketingFrequencies[0].status,
        weekly: paidMarketingFrequencies[1].status,
        monthly: paidMarketingFrequencies[2].status,
      })
    }

    if (bmAllowed) {
      frequencies.push({
        platform: 'BotZapping',
        daily: botZappingFrequencies[0].status,
        weekly: botZappingFrequencies[1].status,
        monthly: botZappingFrequencies[2].status,
      })
    }

    mutate(
      {
        userNotificationsFrequenciesSetup: frequencies,
      },
      { onSuccess }
    )
  }

  return (
    <NotificationsSectionStyled id={'user-profile-notifications-section'}>
      <ProfileSectionTitle>{t('notifications.title')}</ProfileSectionTitle>
      <DescriptionRow1>{t('notifications.product_performance_updates')}</DescriptionRow1>
      <DescriptionRow2>{t('notifications.stay_updated')}</DescriptionRow2>
      <BoxesWrapper>
        {notifications && (
          <>
            {!!pmAllowed && (
              <MultiOption
                title={t('notifications.paid_marketing')}
                status={notifications.paidMarketing}
                options={paidMarketingFrequencies}
                onChange={(value: boolean) => {
                  handleChange(value, 'paidMarketing')
                }}
                onOptionChange={(id, value) => handleOptionChange('paidMarketing', id, value)}
              />
            )}

            {!!bmAllowed && (
              <MultiOption
                title={t('notifications.bot_zapping')}
                status={notifications?.botZapping}
                options={botZappingFrequencies}
                onChange={(value: boolean) => {
                  handleChange(value, 'botZapping')
                }}
                onOptionChange={(id, value) => handleOptionChange('botZapping', id, value)}
              />
            )}
          </>
        )}
      </BoxesWrapper>
      <RealTimeOptions>
        <SwitchOptionsWrapper>
          <Toggle disabled={true} value={true} onChange={() => {}} />
          <SwitchOptionLabel>{t('notifications.system_notifications')}</SwitchOptionLabel>
        </SwitchOptionsWrapper>
        <SwitchOptionsWrapper>
          <Toggle
            value={notifications?.marketingNotifications || false}
            onChange={(value) => {
              handleChange(value, 'marketingNotifications')
            }}
          />
          <SwitchOptionLabel>{t('notifications.marketing_notifications')}</SwitchOptionLabel>
        </SwitchOptionsWrapper>
      </RealTimeOptions>
    </NotificationsSectionStyled>
  )
}
